import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
/**
 * The notification feed — activity about YOU.
 *
 * ── Not the same thing as announcements, and the design says so ───────────
 * The prototype separates them deliberately: the bell is "activity about you"
 * (replies, reactions, reminders), the megaphone is official announcements.
 * They are different endpoints, different shapes and different read semantics,
 * and conflating them is how a member's reply notification ends up in a
 * broadcast list.
 *
 * `MemberAnnouncement` carries no read state at all. This one does.
 */
/**
 * Where a notification leads (8 Oct 2026). Resolved by the API from the
 * event's own ids, so the panel never guesses a route from `data`. Null when
 * there is nowhere to go — or the post has since been hidden or removed.
 */
export const NotificationTarget = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('POST'),
        roomId: z.string().uuid(),
        postId: z.string().uuid(),
        commentId: z.string().uuid().nullable(),
    }),
    z.object({ kind: z.literal('CHALLENGE') }),
    z.object({ kind: z.literal('ANNOUNCEMENT'), announcementId: z.string().uuid() }),
]);
export const Notification = z.object({
    id: z.string().uuid(),
    /** The template this fired from. Our copy keys off it when the body is thin. */
    eventKey: z.string(),
    title: z.string(),
    body: z.string(),
    /**
     * Free-form payload from the event — ids to route to, names to interpolate.
     *
     * Deliberately NOT typed further: the contract declares it as an open record
     * of primitives, and inventing a union here would be a second, wrong
     * definition of what each event carries.
     */
    data: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).nullable(),
    /** Null, or absent from a server that predates it: nowhere to go. */
    target: NotificationTarget.nullish(),
    /** Null until read. A timestamp, not a boolean — the API models when, not whether. */
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * `unread` counts the WHOLE feed, not the page.
 *
 * The contract is explicit about this — "the badge counts every unread
 * notification, not the page being read" — so the badge reads this field and
 * never `items.filter(...)`, which would under-count the moment the feed is
 * longer than one page.
 */
export const NotificationFeed = z.object({
    items: z.array(Notification),
    total: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
});
export function useNotifications(http, opts = {}) {
    const pageSize = opts.pageSize ?? 12;
    return useQuery({
        queryKey: [...queryKeys.notifications.feed, pageSize],
        queryFn: () => http.get('/notifications', { schema: NotificationFeed, query: { pageSize } }),
    });
}
/*
 * `marked`, as the API's `MarkReadResult` spells it. This read `updated` for
 * months, so every mark-read failed the parse after the server had already
 * marked the rows — and the feed was never re-read, leaving the badge stale.
 */
const ReadResult = z.object({ marked: z.number().int().nonnegative() });
const ClearResult = z.object({ cleared: z.number().int().nonnegative() });
/**
 * Mark read. Empty `ids` marks the entire unread feed — that is the contract's
 * own definition of what the "mark all" control sends, not a convention we
 * invented here.
 *
 * Idempotent server-side: an id already read is not counted again. So a double
 * click costs a request and nothing else, and we do not need to guard it.
 */
export function useMarkNotificationsRead(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (ids = []) => http.post('/notifications/read', { ids }, { schema: ReadResult }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed });
        },
    });
}
/**
 * « Tout effacer » — the whole feed leaves, and the badge with it.
 *
 * Nothing is deleted on the server: the rows stay for the catalogue's Results
 * tab and simply stop being this person's. Idempotent there too, so the
 * control needs no guard against a second tap.
 */
export function useClearNotifications(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http.post('/notifications/clear', {}, { schema: ClearResult }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.feed });
        },
    });
}
//# sourceMappingURL=notifications.js.map